// ABOUTME: Presents company-bound MCP consent using the existing signed-in Compleo identity.
// ABOUTME: Requires explicit scope choices and sends the one-use ticket directly back to the issuer.
import React from "react";
import {
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    MenuItem,
    Paper,
    TextField,
    Typography
} from "@material-ui/core";
import { useTranslation } from "react-i18next";
import { useAuthState } from "_ReactContext/AuthContext";
import Page from "Pages/_Layouts/DefaultInternal";
import { followMcpConsentRedirect, mcpPanelCall } from "./api";
import { initialMcpScopes, toggleMcpScope } from "./consent-state";

export const scopeLabels: Record<string, string> = {
    "mcp:context:read": "scopeContext",
    "mcp:jobs:read": "scopeJobs",
    "mcp:candidates:read": "scopeCandidates",
    "mcp:resumes:read": "scopeResumes",
    "mcp:analytics:read": "scopeAnalytics",
    "mcp:reports:export": "scopeExport"
};
interface Details {
    companyId: string;
    clientId: string;
    scopes: string[];
    cancelUrl: string;
    expiresAt: number;
}
export default function McpConsent() {
    const [t] = useTranslation("MCP", { useSuspense: false });
    const { company, allCompanies } = useAuthState();
    const [companyId, setCompanyId] = React.useState(
        String(company.companyId || "")
    );
    const [details, setDetails] = React.useState<Details | null>(null);
    const [scopes, setScopes] = React.useState<string[]>([]);
    const [busy, setBusy] = React.useState(false);
    const [error, setError] = React.useState(false);
    const interactionId =
        new URLSearchParams(window.location.search).get("interaction") || "";
    React.useEffect(() => {
        if (!companyId && company.companyId)
            setCompanyId(String(company.companyId));
    }, [company.companyId, companyId]);
    React.useEffect(() => {
        const controller = new AbortController();
        setDetails(null);
        setScopes([]);
        setError(false);
        if (companyId && /^[A-Za-z0-9_-]{1,256}$/.test(interactionId)) {
            void mcpPanelCall<Details>(
                "/panel/mcp/consent/details",
                { companyId, interactionId },
                controller.signal
            )
                .then((value) => {
                    if (
                        !controller.signal.aborted &&
                        value.companyId === companyId
                    ) {
                        setDetails(value);
                        setScopes(initialMcpScopes(value.scopes));
                    }
                })
                .catch(() => {
                    if (!controller.signal.aborted) setError(true);
                });
        } else if (companyId) setError(true);
        return () => controller.abort();
    }, [companyId, interactionId]);
    async function submit() {
        if (!details || busy) return;
        setBusy(true);
        setError(false);
        try {
            const result = await mcpPanelCall<{ redirectUrl: string }>(
                "/panel/mcp/consent/issue",
                { companyId, interactionId, scopes }
            );
            followMcpConsentRedirect(result.redirectUrl);
        } catch {
            setError(true);
            setBusy(false);
        }
    }
    return (
        <Page featureName={t("consentTitle")} maxWidth="sm">
            <Paper>
                <Box p={3}>
                    <Typography paragraph>{t("consentDescription")}</Typography>
                    <TextField
                        select
                        fullWidth
                        label={t("company")}
                        value={companyId}
                        onChange={(event) => setCompanyId(event.target.value)}
                        disabled={busy}
                    >
                        {(allCompanies || []).map((item: any) => (
                            <MenuItem
                                key={item.pk}
                                value={String(item.pk).split(":")[1]}
                            >
                                {item.CompanyFancyName}
                            </MenuItem>
                        ))}
                    </TextField>
                    {details && (
                        <Box mt={2}>
                            <Typography paragraph>
                                {t("client", { client: details.clientId })}
                            </Typography>
                            {details.scopes.map((scope) => (
                                <Box key={scope}>
                                    <FormControlLabel
                                        label={t(
                                            scopeLabels[scope] ||
                                                "scopeUnavailable"
                                        )}
                                        control={
                                            <Checkbox
                                                checked={scopes.includes(scope)}
                                                disabled={
                                                    busy ||
                                                    scope === "mcp:context:read"
                                                }
                                                onChange={(event) =>
                                                    setScopes((current) =>
                                                        toggleMcpScope(
                                                            current,
                                                            scope,
                                                            event.target
                                                                .checked,
                                                            details.scopes
                                                        )
                                                    )
                                                }
                                            />
                                        }
                                    />
                                </Box>
                            ))}
                            <Typography variant="body2" paragraph>
                                {t("resumeDisclosure")}
                            </Typography>
                            <Typography variant="body2" paragraph>
                                {t("privacyDisclosure")}
                            </Typography>
                            <Typography variant="body2" paragraph>
                                {t("revocationDisclosure")}
                            </Typography>
                        </Box>
                    )}
                    {error && (
                        <Typography color="error" role="alert">
                            {t("unavailable")}
                        </Typography>
                    )}
                    <Box mt={3} display="flex" justifyContent="space-between">
                        <Button
                            disabled={!details || busy}
                            onClick={() =>
                                details &&
                                followMcpConsentRedirect(details.cancelUrl)
                            }
                        >
                            {t("cancel")}
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            disabled={
                                !details ||
                                busy ||
                                !scopes.includes("mcp:context:read")
                            }
                            onClick={submit}
                        >
                            {t(busy ? "working" : "authorize")}
                        </Button>
                    </Box>
                </Box>
            </Paper>
        </Page>
    );
}
